import {
    AddressInfo,
    CarInfo,
    CBPOnboard,
    ContractDetails,
    DriverDetails,
    EmergencyContactInfo,
    PersonalInfo,
    SplendInitiatives,
    UsedVehicleAddendumDetails,
} from "@context/UserJourneyContext";
import { PlanOptions } from "@utils/planOptions";
import { currentTime_HHMM, endOfDayTime_HHMM, flipDateFormat } from "@utils/misc/functions";
import { ICustomerPotential, SplendInitiative } from "@services/customer/CustomerPotentials";
import { logger as sentryLogger } from '@sentry/react'
import { TAddon, TCHBLivePrices } from "@services/previewInvoice/PreviewInvoiceService";
import { PricingRecordLineItem } from "@services/quotes/PricingRecords";
import { QuoteAddon } from "@services/signatureDocument/SignatureDocumentService";
import { IPricingRecordLineItem } from "@services/QuoteBuilder/QuoteBuilderService";
import { text } from "stream/consumers";
import { getCoupons, getPlanWeeklyFee, getTotalWeeklySubscriptionPrice } from "@utils/pricingRecordHelpers";


export class AddonUtils {
    public static updateQuoteAddonsWithLivePrices(quoteAddons: PricingRecordLineItem[], live_prices: TCHBLivePrices): PricingRecordLineItem[] {
        if (!live_prices || !quoteAddons || quoteAddons.length === 0) {
            return quoteAddons;
        }

        // Create a combined array of all live pricing items (plan + addons)
        const allLivePrices: any[] = [];

        // Add plan if it exists
        if (live_prices.plan) {
            allLivePrices.push({
                id: live_prices.plan.id,
                total_exc_gst: live_prices.plan.total_exc_gst,
                total_inc_gst: live_prices.plan.total_inc_gst
            });
        }

        // Add addons if they exist
        if (live_prices.addons && Array.isArray(live_prices.addons)) {
            allLivePrices.push(...live_prices.addons);
        }

        if (allLivePrices.length === 0) {
            sentryLogger.debug("No live prices found so using info from Pricing Record",{ allLivePrices});
            return quoteAddons;
        }

        // Update quoteAddons with live prices
        return quoteAddons.map(addon => {
            // Find matching live price by chargebee_item_key
            const livePrice = allLivePrices.find((price: any) =>
                price.id === addon.chargebee_item_key
            );

            if (livePrice && livePrice.total_exc_gst !== undefined) {
                const updatedAddon = {
                    ...addon,
                    price_excluding_gst: parseFloat(livePrice.total_exc_gst)
                };

                // Also update price_including_gst if total_inc_gst is available
                if (livePrice.total_inc_gst !== undefined) {
                    updatedAddon.price_including_gst = parseFloat(livePrice.total_inc_gst);
                }

                return updatedAddon;
            }
            return addon;
        });
    }

    public static extrapolateAddonsAsFieldTextData(quoteAddons: PricingRecordLineItem[]): { [key: string]: number | string} {
        /**
         * Filters quoteAddons for included items and creates field text data entries.
         * The key is the line_item_label_code and the value is the price_excluding_gst.
         * Be aware that this function will overwrite existing keys in text_data. Meaning that if for some reason
         * you put A1 as a key earlier in the payload this will overwrite it.
         */
        const fieldTextData: { [key: string]: number | string } = {};

        quoteAddons
            .forEach((item: PricingRecordLineItem) => {
                if (item.line_item_label_code && item.price_excluding_gst !== null && item.price_excluding_gst !== undefined) {
                    if (item.included === true) {
                        fieldTextData[item.line_item_label_code] = item.price_excluding_gst;
                    } else if (item.line_item_label_code !== "C2") {
                        fieldTextData[item.line_item_label_code] = "N/A";
                    }
                }
            });

        // Special handling for C2: set to "N/A" only if no C2 addon is included
        const hasIncludedC2 = quoteAddons.some(addon =>
            addon.line_item_label_code === "C2" && addon.included === true
        );
        const hasC2 = quoteAddons.some(addon => addon.line_item_label_code === "C2");

        if (hasC2 && !hasIncludedC2) {
            fieldTextData["C2"] = "N/A";
        }

        return fieldTextData;
    }

    public static calculateCategoryTotals(quoteAddons: PricingRecordLineItem[]): { [key: string]: number } {
        const categoryTotals: { [key: string]: number } = {
            category_a_total: 0,
            category_b_total: 0,
            category_c_total: 0
        };

        quoteAddons
            .filter((item: PricingRecordLineItem) => item.included === true)
            .forEach((item: PricingRecordLineItem) => {
                if (item.line_item_label_code && item.price_excluding_gst !== null && item.price_excluding_gst !== undefined) {
                    const firstChar = item.line_item_label_code.charAt(0).toLowerCase();
                    if (firstChar === 'a') {
                        categoryTotals.category_a_total += Number(item.price_excluding_gst);
                    } else if (firstChar === 'b') {
                        categoryTotals.category_b_total += Number(item.price_excluding_gst);
                    } else if (firstChar === 'c') {
                        categoryTotals.category_c_total += Number(item.price_excluding_gst);
                    }
                }
            });

        categoryTotals.category_a_total = parseFloat(categoryTotals.category_a_total.toFixed(2));
        categoryTotals.category_b_total = parseFloat(categoryTotals.category_b_total.toFixed(2));
        categoryTotals.category_c_total = parseFloat(categoryTotals.category_c_total.toFixed(2));

        categoryTotals.all_categories_total = categoryTotals.category_a_total + categoryTotals.category_b_total + categoryTotals.category_c_total;
        categoryTotals.all_categories_total = parseFloat(categoryTotals.all_categories_total.toFixed(2));

        return categoryTotals;
    }

    public static calculateCategoryTotalsIncGst(quoteAddons: PricingRecordLineItem[]): { [key: string]: number } {
        const categoryTotals: { [key: string]: number } = {
            category_a_total: 0,
            category_b_total: 0,
            category_c_total: 0
        };

        quoteAddons
            .filter((item: PricingRecordLineItem) => item.included === true)
            .forEach((item: PricingRecordLineItem) => {
                if (item.line_item_label_code && item.price_including_gst !== null && item.price_including_gst !== undefined) {
                    const firstChar = item.line_item_label_code.charAt(0).toLowerCase();
                    if (firstChar === 'a') {
                        categoryTotals.category_a_total += Number(item.price_including_gst);
                    } else if (firstChar === 'b') {
                        categoryTotals.category_b_total += Number(item.price_including_gst);
                    } else if (firstChar === 'c') {
                        categoryTotals.category_c_total += Number(item.price_including_gst);
                    }
                }
            });

        categoryTotals.category_a_total = parseFloat(categoryTotals.category_a_total.toFixed(2));
        categoryTotals.category_b_total = parseFloat(categoryTotals.category_b_total.toFixed(2));
        categoryTotals.category_c_total = parseFloat(categoryTotals.category_c_total.toFixed(2));

        categoryTotals.all_categories_total = categoryTotals.category_a_total + categoryTotals.category_b_total + categoryTotals.category_c_total;
        categoryTotals.all_categories_total = parseFloat(categoryTotals.all_categories_total.toFixed(2));

        return categoryTotals;
    }
}

/**
 * @returns time as string formatted as DD Month YYYY
 */
const convertDateForZohoSign = (date: string): string => {
    return new Date(date).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
};


export class PlanPayload {
    prefill_data: any;
    contact_owner_id: string | undefined;
    execution_date: string | undefined;
    licence_expiry: string | undefined;
    contact_id: string | undefined;
    first_name: string | undefined;
    middle_name: string | undefined;
    last_name: string | undefined;
    street: string | undefined;
    postcode: string | undefined;
    province: string | undefined;
    end_date: string | undefined;
    plan: string | undefined;
    plan_id: string | undefined;
    start_date: string | undefined;
    dob: string | undefined;
    city: string | undefined;
    vehicle_id: string | undefined;
    national_insurance_number: string | undefined;
    pco_licence_number: string | undefined;
    pco_licence_expiry: string | undefined;
    vehicle_location: string | undefined;
    potential_id: string | undefined;
    ipb_discount_amount: number | undefined;
    phav_id: string | undefined;

    constructor(
        driverDetails: any,
        personalInfo: any,
        carInfo: any,
        contractDetails: any,
        addressInfo: any,
        selectedPotential: ICustomerPotential
    ) {
        this.prefill_data = {
            field_text_data: {
                plan: driverDetails.plan,
                hirer: `${personalInfo.firstName}${personalInfo.middleName ? ' ' + personalInfo.middleName : ''} ${personalInfo.lastName}`,
                vehicle: `${carInfo.year} ${carInfo.make} ${carInfo.model} VIN: ${carInfo.vin} Registration: ${carInfo.registrationPlate}`,
                fleetio_id: carInfo.id,
                vin: carInfo.vin,
                registration: carInfo.registrationPlate,
                year: carInfo.year,
                make: carInfo.make,
                model: carInfo.model,
                colour: carInfo.colour,
                body: carInfo.bodyType,
                odometer: String(carInfo.odometer),
                driver_licence_number: driverDetails.driversLicenceNumber,
                licence_state: driverDetails.province,
                email: personalInfo.email,
                crmId: driverDetails.crmId,
                state: addressInfo.state,
                start_datetime: `${convertDateForZohoSign(contractDetails.subsStartDate)} ${currentTime_HHMM()}`,
                start_time: currentTime_HHMM(),
                start_date: convertDateForZohoSign(contractDetails.subsStartDate),
                end_date: convertDateForZohoSign(contractDetails.subsEndDate),
                end_datetime: `${convertDateForZohoSign(contractDetails.subsEndDate)} ${endOfDayTime_HHMM()}`,
                end_time: endOfDayTime_HHMM()
            },
            // Legacy fields, probably not used anymore. Please check with the ZohoSign templates before removal.
            field_date_data: {
                licence_expiry: convertDateForZohoSign(driverDetails.driversLicenceExpiry),
                dob: convertDateForZohoSign(driverDetails.dateOfBirth),
                start_date: convertDateForZohoSign(contractDetails.subsStartDate),
                execution_date: convertDateForZohoSign(contractDetails.executionDate),
            },
            field_boolean_data: {}
        };
        this.plan = driverDetails.plan;
        this.plan_id = contractDetails.planId;
        this.start_date = contractDetails.subsStartDate;
        this.dob = driverDetails.dateOfBirth;
        this.vehicle_id = carInfo.id;
        this.city = addressInfo.city;
        this.execution_date = contractDetails.executionDate;
        this.licence_expiry = driverDetails.driversLicenceExpiry;
        this.contact_id = driverDetails.crmId;
        this.first_name = personalInfo.firstName;
        this.middle_name = personalInfo.middleName;
        this.last_name = personalInfo.lastName;
        this.street = addressInfo.street;
        this.postcode = addressInfo.postcode;
        this.province = driverDetails.province;
        this.potential_id = selectedPotential.id;

        let textData = this.prefill_data.field_text_data;

        if (personalInfo.alternativeName) {
            textData.alternative_name = personalInfo.alternativeName;
        }
    }
};


class RAVCreditHirePayload extends PlanPayload {

    constructor(
        driverDetails: any,
        personalInfo: any, carInfo: any,
        contractDetails: any,
        addressInfo: any,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;

        textData.fleetio_id = carInfo.id;
        textData.daily_fee = contractDetails.planDailyFee;
        textData.weekly_allowance = contractDetails.planWeeklyDistAllowance;
        textData.weekly_fee = contractDetails.planWeeklyFee;
        textData.weekly_km_charge = contractDetails.planAdditionalDistCharge;
        textData.agreement_title = `${this.plan} Agreement`;
        textData.vehicle_assignment_title = `${this.plan} Assignment Form`;
        textData.licence_expiry_text = flipDateFormat(driverDetails.driversLicenceExpiry);
        textData.dob_text = flipDateFormat(driverDetails.dateOfBirth);
        // Field supposed to be removed once the start_date/start_datetime fields (from parent class)
        // are made available through deployment. Make sure the ZohoSign template is updated before
        // removing this.
        textData.start_date_text = flipDateFormat(contractDetails.subsStartDate);
    }
};

class PHAVPayload extends PlanPayload {

    constructor(
        driverDetails: any,
        personalInfo: any, carInfo: any,
        contractDetails: any,
        addressInfo: any,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;

        textData.licence_expiry_text = flipDateFormat(driverDetails.driversLicenceExpiry);
        textData.dob_text = flipDateFormat(driverDetails.dateOfBirth);
        // Field supposed to be removed once the start_date/start_datetime fields (from parent class)
        // are made available through deployment. Make sure the ZohoSign template is updated before
        // removing this.
        textData.start_date_text = flipDateFormat(contractDetails.subsStartDate);
        textData.contact_owner = contractDetails.contactOwner;
        textData.address = `${addressInfo.street}, ${addressInfo.city}, ${addressInfo.postcode}`;
        textData.weekly_fee = contractDetails.planWeeklyFee;
        textData.daily_fee = contractDetails.planDailyFee;

        this.phav_id = driverDetails.phavModuleId;
        this.contact_owner_id = contractDetails.contactOwnerId;
        this.province = "London"; // this will always be London for PHAV


    }
};

class RAVSharecoverPayload extends PlanPayload {
    constructor(driverDetails: any, personalInfo: any, carInfo: any, contractDetails: any, addressInfo: any,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;

        textData.fleetio_id = carInfo.id;
        textData.daily_fee = contractDetails.planDailyFee;
        textData.weekly_allowance = contractDetails.planWeeklyDistAllowance;
        textData.weekly_fee = contractDetails.planWeeklyFee;
        textData.weekly_km_charge = contractDetails.planAdditionalDistCharge;
        textData.agreement_title = `${driverDetails.plan} Agreement`;
        textData.vehicle_assignment_title = `${driverDetails.plan} Assignment Form`;
        textData.licence_expiry_text = flipDateFormat(driverDetails.driversLicenceExpiry);
        textData.dob_text = flipDateFormat(driverDetails.dateOfBirth);
        // Field supposed to be removed once the start_date/start_datetime fields (from parent class)
        // are made available through deployment. Make sure the ZohoSign template is updated before
        // removing this.
        textData.start_date_text = flipDateFormat(contractDetails.subsStartDate);
    }
};


class RAVShortTermRentalPayload extends PlanPayload {
    constructor(driverDetails: any, personalInfo: any, carInfo: any, contractDetails: any, addressInfo: any,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;

        textData.abn = personalInfo.hirerABN;
        textData.minimum_term = contractDetails.planMinimumDailyPeriod;
        textData.weekly_fee = contractDetails.planWeeklyFee;
        textData.weekly_allowance = contractDetails.planWeeklyDistAllowance;
        textData.weekly_km_charge = contractDetails.planAdditionalDistCharge;
        textData.gender = personalInfo.gender;
        textData.nationality = personalInfo.nationality;
        textData.mobile = personalInfo.mobile;
        textData.address = `${addressInfo.street}, ${addressInfo.city}, ${addressInfo.state}, ${addressInfo.postcode}`;
        textData.licence_expiry_text = flipDateFormat(driverDetails.driversLicenceExpiry);
        textData.dob_text = flipDateFormat(driverDetails.dateOfBirth);
        // Field supposed to be removed once the start_date/start_datetime fields (from parent class)
        // are made available through deployment. Make sure the ZohoSign template is updated before
        // removing this.
        textData.start_date_text = flipDateFormat(contractDetails.subsStartDate);
    }
};


class FlexiPayload extends PlanPayload {

    weekly_kilometre_allowance: any;
    minimum_period_weeks: any;
    splend_initiative: any;
    trip_discount: any;
    available_vehicle_discount: any;

    constructor(driverDetails: any, personalInfo: any, carInfo: any, contractDetails: any, addressInfo: any, emergencyContactInfo: any, splendInitiatives: any,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;

        // Additional fields for Flexi plans
        textData.weekly_fee = String(contractDetails.planWeeklyFee);
        textData.setup_fee = String(contractDetails.planSetupFee);
        // For V2, distance allowance will be constant regardless of the splend initiative
        textData.weekly_allowance = String(contractDetails.planWeeklyDistAllowance);
        textData.additional_charge = String(contractDetails.planAdditionalDistCharge);
        textData.cover_by = contractDetails.insuranceCover;
        textData.plan_length = String(contractDetails.planLen);
        textData.contact_owner = contractDetails.contactOwner;
        // TODO we can remove this field as it is no longer part of the onboarding journey.
        //  The field also needs to be deleted from the AU flexi contract
        textData.additional_driver_fee = contractDetails.additionalDriverFee;
        textData.vehicle_description = `${carInfo.year} ${carInfo.make} ${carInfo.model} VIN: ${carInfo.vin} Registration: ${carInfo.registrationPlate}`;
        textData.dob = convertDateForZohoSign(driverDetails.dateOfBirth);
        textData.licence_expiry = convertDateForZohoSign(driverDetails.driversLicenceExpiry);
        textData.minimum_period_weeks = String(contractDetails.planMinimumPeriod);

        // Emergency contact information
        textData.primary_emergency_name = emergencyContactInfo.primaryName;
        textData.primary_emergency_address = emergencyContactInfo.primaryAddress;
        textData.primary_emergency_phone = emergencyContactInfo.primaryPhone;
        textData.primary_emergency_relationship = emergencyContactInfo.primaryRelationship;
        textData.secondary_emergency_name = emergencyContactInfo.secondaryName;
        textData.secondary_emergency_address = emergencyContactInfo.secondaryAddress;
        textData.secondary_emergency_phone = emergencyContactInfo.secondaryPhone;
        textData.secondary_emergency_relationship = emergencyContactInfo.secondaryRelationship;

        // Address information
        textData.address = `${addressInfo.street} ${addressInfo.city} ${addressInfo.state} ${addressInfo.postcode}`;
        textData.gender = personalInfo.gender;
        textData.nationality = personalInfo.nationality;
        textData.toll_tag_number = personalInfo.tollTagNumber;
        textData.toll_tag_account_number = personalInfo.tollTagAccountNumber;
        textData.toll_tag_issuer = personalInfo.tollTagIssuer;
        textData.abn = personalInfo.hirerABN;

        // Date fields
        this.prefill_data.field_date_data.end_date = convertDateForZohoSign(contractDetails.subsEndDate);

        this.province = addressInfo.state;
        this.contact_owner_id = contractDetails.contactOwnerId;
        this.end_date = contractDetails.subsEndDate;

        if (selectedPotential.splend_initiative === SplendInitiative.Ignition) {
            this.splend_initiative = selectedPotential.splend_initiative;
        }

        if (selectedPotential.splend_initiative === SplendInitiative.DynamicPricing) {
            this.splend_initiative = selectedPotential.splend_initiative;
            textData.trip_count = splendInitiatives.tripCount;
            textData.trip_discount = splendInitiatives.tripDiscount;
            // The two fields below - weekly_distance_... have been introduced to accommodate both AU and UK.
            // Depending on the country for which the journey is done, just one will be used by Zoho Sign and
            // splend backend.
            // The intention is to reach to a common name and to keep just one for the future.
            textData.weekly_distance_allowance_kms = splendInitiatives.weeklyDistanceAllowance;
            textData.weekly_distance_allowance_miles = splendInitiatives.weeklyDistanceAllowance;
            textData.weekly_distance_allowance_discount = splendInitiatives.weeklyDistanceAllowanceDiscount;
            textData.minimum_period_discount = splendInitiatives.minimumPeriodWeeksDiscount;
            textData.available_vehicle = splendInitiatives.vehicleUtilisation;
            textData.available_vehicle_discount = splendInitiatives.availableVehicleDiscount;
            textData.total_weekly_discount = String(splendInitiatives.totalDynamicPricing);
            textData.weekly_fee = String(splendInitiatives.adjustedWeeklyHireFee);
            textData.minimum_period_weeks = splendInitiatives.minimumPeriodWeeks;
        }
    }

};


class FlexiV2Payload extends PlanPayload {

    splend_initiative: any;
    pricing_record_id: string;
    couponIds: string[];

    constructor(
        driverDetails: any, personalInfo: any, carInfo: any,
        contractDetails: any, addressInfo: any, emergencyContactInfo: any,
        splendInitiatives: any, selectedPotential: ICustomerPotential,
        cbpOnboard: CBPOnboard,
    ) {
        super(
            driverDetails, personalInfo, carInfo, contractDetails, addressInfo,
            selectedPotential
        );

        // Update quoteAddons with live prices before using them for calculations
        // FIXME: These live prices should really be handled in the backend rather than the frontend
        const updatedQuoteAddons = AddonUtils.updateQuoteAddonsWithLivePrices(
            cbpOnboard.quoteAddons,
            cbpOnboard.live_prices
        );
        cbpOnboard.quoteAddons = updatedQuoteAddons;
        // Extract chargebee_item_key from the coupons so that payload would end up only with the ids only
        const coupons = getCoupons(cbpOnboard.quoteAddons);
        this.couponIds = coupons.map((coupon) => coupon.chargebee_item_key);

        let textData = this.prefill_data.field_text_data;
        textData.contact_owner = contractDetails.contactOwner;
        this.contact_owner_id = contractDetails.contactOwnerId;
        this.plan_id = cbpOnboard.PlanId;
        this.pricing_record_id = cbpOnboard.PricingRecordId;

        // FIXME: The following are unused in AU but could be essential in Flexi UK.
        // Keeping it here for review once Flexi v2.0 UK is prioritised.
        // this.prefill_data.field_date_data.end_date = convertDateForZohoSign(contractDetails.subsEndDate);
        // this.end_date = contractDetails.subsEndDate;
        // this.province = addressInfo.state;

        // Additional fields for Flexi plans
        textData.weekly_fee = String(getPlanWeeklyFee(cbpOnboard.quoteAddons));
        textData.setup_fee = String(cbpOnboard.setupFeeIncludingGst);
        // For V2, distance allowance will be constant regardless of the splend initiative
        textData.weekly_allowance = String(cbpOnboard.WeeklyDistanceAllowance);  
        textData.additional_charge = String(cbpOnboard.ExcessDistanceCost);
        textData.additional_driver_fee = contractDetails.additionalDriverFee;
        textData.vehicle_description = `${carInfo.year} ${carInfo.make} ${carInfo.model} VIN: ${carInfo.vin} Registration: ${carInfo.registrationPlate}`;
        textData.dob = convertDateForZohoSign(driverDetails.dateOfBirth);
        textData.licence_expiry = convertDateForZohoSign(driverDetails.driversLicenceExpiry);
        textData.minimum_period_weeks = String(cbpOnboard.MinimumTerm);

        // Emergency contact information
        textData.primary_emergency_name = emergencyContactInfo.primaryName;
        textData.primary_emergency_address = emergencyContactInfo.primaryAddress;
        textData.primary_emergency_phone = emergencyContactInfo.primaryPhone;
        textData.primary_emergency_relationship = emergencyContactInfo.primaryRelationship;
        textData.secondary_emergency_name = emergencyContactInfo.secondaryName;
        textData.secondary_emergency_address = emergencyContactInfo.secondaryAddress;
        textData.secondary_emergency_phone = emergencyContactInfo.secondaryPhone;
        textData.secondary_emergency_relationship = emergencyContactInfo.secondaryRelationship;

        // Address information
        textData.address = `${addressInfo.street} ${addressInfo.city} ${addressInfo.state} ${addressInfo.postcode}`;
        textData.gender = personalInfo.gender;
        textData.nationality = personalInfo.nationality;
        textData.abn = personalInfo.hirerABN;

        if (selectedPotential.splend_initiative === SplendInitiative.Ignition) {
            this.splend_initiative = selectedPotential.splend_initiative;
            textData.total_weekly_discount = String(splendInitiatives.additionalDiscount);
        }
        else if (selectedPotential.splend_initiative === SplendInitiative.DynamicPricing) {
            // The assignments in this code block are USED to writeback information
            // into Zoho POTENTIAL / SUBSCRIPTION but NOT USED when generating a contract
            this.splend_initiative = selectedPotential.splend_initiative;
            textData.trip_count = cbpOnboard.TripCount;
            textData.trip_discount = splendInitiatives.tripDiscount;
            textData.total_weekly_discount = String(splendInitiatives.totalDynamicPricing);

            // Overwriting the weekly fee with the calculated total from plan and dynamic pricing coupon
            // as the contract needs to reflect the discounted amount and the dynamic pricing coupon has
            // indefinite duration.
            textData.weekly_fee = String(getTotalWeeklySubscriptionPrice(cbpOnboard.quoteAddons));

            // The two fields below - weekly_distance_... have been introduced to accommodate both AU and UK.
            // Depending on the country for which the journey is done, just one will be used by Zoho Sign and
            // splend backend.
            // The intention is to reach to a common name and to keep just one for the future.
            textData.weekly_distance_allowance_kms = cbpOnboard.WeeklyDistanceAllowance;
            textData.weekly_distance_allowance_miles = cbpOnboard.WeeklyDistanceAllowance;

            // FIXME: The following fields unused in Contract generation and
            // are not significant anymore but is still used to writeback info
            // to Zoho Potential/Subscription. Recommended to clean up in the future.
            textData.available_vehicle = splendInitiatives.vehicleUtilisation || "";
            textData.available_vehicle_discount = splendInitiatives.availableVehicleDiscount || "";
        }
    }
}

class SplendFlexiUKPayload extends PlanPayload {

    weekly_kilometre_allowance: any;
    minimum_period_weeks: any;
    splend_initiative: any;
    trip_discount: any;
    available_vehicle_discount: any;

    constructor(
        driverDetails: DriverDetails,
        personalInfo: PersonalInfo,
        carInfo: CarInfo,
        contractDetails: ContractDetails,
        addressInfo: AddressInfo,
        emergencyContactInfo: EmergencyContactInfo,
        splendInitiatives: SplendInitiatives,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;

        // Additional fields for Flexi plans
        textData.weekly_fee = String(contractDetails.planWeeklyFee);
        textData.setup_fee = String(contractDetails.planSetupFee);
        textData.weekly_allowance = String(contractDetails.planWeeklyDistAllowance);
        textData.additional_charge = String(contractDetails.planAdditionalDistCharge);
        // cover_by may not be needed as contract template seem to not have such field
        textData.cover_by = contractDetails.insuranceCover;
        textData.plan_length = String(contractDetails.planLen);
        textData.contact_owner = contractDetails.contactOwner;
        textData.additional_driver_fee = contractDetails.additionalDriverFee;
        textData.vehicle_description = `${carInfo.year} ${carInfo.make} ${carInfo.model} VIN: ${carInfo.vin} Registration: ${carInfo.registrationPlate}`;
        textData.dob = convertDateForZohoSign(driverDetails.dateOfBirth);
        textData.dob_text = flipDateFormat(driverDetails.dateOfBirth);
        textData.licence_expiry = convertDateForZohoSign(driverDetails.driversLicenceExpiry);
        textData.licence_expiry_text = flipDateFormat(driverDetails.driversLicenceExpiry);
        textData.minimum_period_weeks = String(contractDetails.planMinimumPeriod);
        textData.employment_status = personalInfo.employmentStatus;
        textData.licence_country = driverDetails.licenceCountry;

        // Emergency contact information
        textData.primary_emergency_name = emergencyContactInfo.primaryName;
        textData.primary_emergency_address = emergencyContactInfo.primaryAddress;
        textData.primary_emergency_phone = emergencyContactInfo.primaryPhone;
        textData.primary_emergency_relationship = emergencyContactInfo.primaryRelationship;
        textData.secondary_emergency_name = emergencyContactInfo.secondaryName;
        textData.secondary_emergency_address = emergencyContactInfo.secondaryAddress;
        textData.secondary_emergency_phone = emergencyContactInfo.secondaryPhone;
        textData.secondary_emergency_relationship = emergencyContactInfo.secondaryRelationship;

        // Address information
        textData.address = `${addressInfo.street} ${addressInfo.city} ${addressInfo.postcode}`;
        textData.gender = personalInfo.gender;
        textData.nationality = personalInfo.nationality;
        textData.time_at_address_years = addressInfo.timeAtAddressYears;
        textData.time_at_address_months = addressInfo.timeAtAddressMonths;


        // Date fields
        this.prefill_data.field_date_data.end_date = convertDateForZohoSign(contractDetails.subsEndDate);

        this.province = addressInfo.state;
        this.contact_owner_id = contractDetails.contactOwnerId;
        this.end_date = contractDetails.subsEndDate;

        if (selectedPotential.splend_initiative === SplendInitiative.Ignition) {
            this.splend_initiative = selectedPotential.splend_initiative;
            textData.subs_start_date = convertDateForZohoSign(contractDetails.subsStartDate);
            textData.ignition_end_date = convertDateForZohoSign(splendInitiatives.ignitionEndDate);
            textData.ignition_discount = `${splendInitiatives.additionalDiscount}`
        }

        if (selectedPotential.splend_initiative === SplendInitiative.DynamicPricing) {
            this.splend_initiative = selectedPotential.splend_initiative;
            textData.trip_count = splendInitiatives.tripCount;
            textData.trip_discount = splendInitiatives.tripDiscount;
            textData.weekly_distance_allowance_kms = splendInitiatives.weeklyDistanceAllowance;
            textData.weekly_distance_allowance_miles = splendInitiatives.weeklyDistanceAllowance;
            textData.weekly_distance_allowance_discount = splendInitiatives.weeklyDistanceAllowanceDiscount;
            textData.minimum_period_discount = splendInitiatives.minimumPeriodWeeksDiscount;
            textData.available_vehicle = splendInitiatives.vehicleUtilisation;
            textData.available_vehicle_discount = splendInitiatives.availableVehicleDiscount;
            textData.total_weekly_discount = String(splendInitiatives.totalDynamicPricing);
            textData.weekly_fee = String(splendInitiatives.adjustedWeeklyHireFee);
            textData.minimum_period_weeks = splendInitiatives.minimumPeriodWeeks;
            textData.driver_rating = splendInitiatives.driverRating; // new
            textData.weekly_allowance = String(splendInitiatives.weeklyDistanceAllowance);

        }
        this.national_insurance_number = personalInfo.nationalInsuranceNumber;
        this.pco_licence_number = driverDetails.pcoLicenseNumber;
        this.pco_licence_expiry = driverDetails.pcoLicenceExpiryDate;
        this.vehicle_location = carInfo.hub;
        // todo is this used in zoho sign contracts ?
    }
};


class SplendFlexiOwnPayload extends PlanPayload {
    constructor(driverDetails: any, personalInfo: any, carInfo: any, contractDetails: any, addressInfo: any, emergencyContactInfo: any, usedVehicleAddendum: any,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;

        textData.end_date_text = flipDateFormat(contractDetails.subsEndDate);
        textData.licence_expiry_text = flipDateFormat(driverDetails.driversLicenceExpiry);
        textData.dob_text = flipDateFormat(driverDetails.dateOfBirth);
        // start_date_text supposed to be removed once the start_date/start_datetime fields (from parent class)
        // are made available through deployment. Make sure the ZohoSign template is updated before
        // removing this.
        textData.start_date_text = flipDateFormat(contractDetails.subsStartDate);
        textData.execution_date_text = flipDateFormat(contractDetails.executionDate);

        // Adding fields specific to SplendFlexiOwn plan
        textData.vehicle_description = `${carInfo.year} ${carInfo.make} ${carInfo.model} VIN: ${carInfo.vin} Registration: ${carInfo.registrationPlate}`;
        textData.type = driverDetails.plan;
        textData.primary_emergency_name = emergencyContactInfo.primaryName;
        textData.primary_emergency_address = emergencyContactInfo.primaryAddress;
        textData.primary_emergency_phone = emergencyContactInfo.primaryPhone;
        textData.primary_emergency_relationship = emergencyContactInfo.primaryRelationship;
        textData.secondary_emergency_name = emergencyContactInfo.secondaryName;
        textData.secondary_emergency_address = emergencyContactInfo.secondaryAddress;
        textData.secondary_emergency_phone = emergencyContactInfo.secondaryPhone;
        textData.secondary_emergency_relationship = emergencyContactInfo.secondaryRelationship;
        textData.address = `${addressInfo.street} ${addressInfo.city} ${addressInfo.state} ${addressInfo.postcode}`;
        textData.gender = personalInfo.gender;
        textData.nationality = personalInfo.nationality;
        textData.toll_tag_number = personalInfo.tollTagNumber;
        textData.toll_tag_account_number = personalInfo.tollTagAccountNumber;
        textData.toll_tag_issuer = personalInfo.tollTagIssuer;
        textData.abn = personalInfo.hirerABN;
        textData.weekly_fee = String(contractDetails.planWeeklyFee);
        textData.setup_fee = String(contractDetails.planSetupFee);
        textData.weekly_allowance = String(contractDetails.planWeeklyDistAllowance);
        textData.additional_charge = String(contractDetails.planAdditionalDistCharge);
        textData.cover_by = contractDetails.insuranceCover;
        textData.plan_length = String(contractDetails.planLen);
        textData.contact_owner = contractDetails.contactOwner;
        textData.nsw_ev_incentive_value_before_discount = contractDetails.NSWIncentivePlanBeforeDiscount;
        textData.nsw_ev_incentive_total = contractDetails.NSWIncentiveTotalValue;
        textData.minimum_period_weeks = String(contractDetails.planMinimumPeriod);
        textData.time_at_address_years = personalInfo.timeAtAddressYears;
        textData.time_at_address_months = personalInfo.timeAtAddressMonths;
        textData.employment_status = personalInfo.employmentStatus;
        textData.licence_country = driverDetails.licenceCountry;
        // Adding Insurance Price Beat related pre-filled text fields 
        textData.registration = carInfo.registrationPlate;
        textData.ipb_discounted_weekly_subscription = (contractDetails.planWeeklyFee - contractDetails.ipbDiscountAmount) || 0;

        // Adding additional fields directly to the object
        this.contact_owner_id = contractDetails.contactOwnerId;
        this.execution_date = contractDetails.executionDate;
        this.licence_expiry = driverDetails.driversLicenceExpiry;
        this.contact_id = driverDetails.crmId;
        this.first_name = personalInfo.firstName;
        this.middle_name = personalInfo.middleName;
        this.last_name = personalInfo.lastName;
        this.street = addressInfo.street;
        this.postcode = addressInfo.postcode;
        this.province = addressInfo.state;
        this.end_date = contractDetails.subsEndDate;
        this.vehicle_id = carInfo.id;
        this.city = addressInfo.city;
        this.dob = driverDetails.dateOfBirth;
        this.start_date = contractDetails.subsStartDate;
        this.ipb_discount_amount = contractDetails.ipbDiscountAmount;
    }
};


class FlexiOwnV2 extends SplendFlexiOwnPayload {
    quoteAddons: QuoteAddon[];
    pricing_record_id: string;

    constructor(driverDetails: any, personalInfo: any, carInfo: any, contractDetails: any, addressInfo: any, emergencyContactInfo: any, usedVehicleAddendum: any,
        selectedPotential: ICustomerPotential, cbpOnboard: CBPOnboard
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, usedVehicleAddendum, selectedPotential);

        // Update quoteAddons with live prices before using them for calculations
        const updatedQuoteAddons = AddonUtils.updateQuoteAddonsWithLivePrices(cbpOnboard.quoteAddons, cbpOnboard.live_prices);
        cbpOnboard.quoteAddons = updatedQuoteAddons;

        let textData = this.prefill_data.field_text_data;

        textData.mobile = personalInfo.mobile;
        textData.plan = cbpOnboard.Plan;
        textData.make_and_model = cbpOnboard.Make + " " + cbpOnboard.Model;
        textData.body_type = carInfo.bodyType;
        textData.plan_length = cbpOnboard.TermLength;
        textData.end_date_text = convertDateForZohoSign(contractDetails.subsEndDate);

        textData.vehicle_driveaway_price = cbpOnboard.vehicleDriveawayPrice;
        textData.replacement_tyres = cbpOnboard.ReplacementTyres;
        textData.replacement_brake_pads = cbpOnboard.ReplacementBrakePadSet;
        textData.brake_or_machined_discs = cbpOnboard.ReplacementOrMachiningOfBrakeDiscs;
        // textData.nsw_weekly_incentive_excl_gst = contractDetails.NSWWeeklyIncentiveExclGST;  //no longer in template
        textData.nsw_total_incentive_excl_gst = contractDetails.NSWTotalIncentiveExclGST;
        textData.reduced_term_length_weeks = cbpOnboard.ReducedTermLengthWeeks;
        textData.weekly_distance_allowance = cbpOnboard.WeeklyDistanceAllowance;
        textData.excess_distance_cost = cbpOnboard.ExcessDistanceCost;
        textData.setup_fee_excluding_gst = cbpOnboard.setupFeeExcludingGst;
        textData.loss_and_damage_waiver_value = cbpOnboard.LossAndDamageWaiverValue;
        textData.reduced_subscription_term = cbpOnboard.TermReduction;

        this.plan_id = cbpOnboard.PlanId; // Overrides
        // QuoteAddon mirrors the structure the BE expects for the signature document addons.
        this.quoteAddons = cbpOnboard.quoteAddons.filter(
            (item: PricingRecordLineItem) => item.included == true
        ).map((item: PricingRecordLineItem) => {
            return {
                plan_item_type: item.chargebee_item_type,
                plan_item_key: item.chargebee_item_key,
                plan_component: item.chargebee_component,
                quantity: 1
            }
        });
        const addonTextData = AddonUtils.extrapolateAddonsAsFieldTextData(cbpOnboard.quoteAddons);
        Object.assign(textData, addonTextData);

        const c2: number = Number(textData.C2);
        if (!Number.isNaN(c2)){
            textData.ipb_c2_discounted = String((c2 - contractDetails.ipbDiscountAmount).toFixed(2));
        } else{
            textData.ipb_c2_discounted = "N/A";
        }
        
        const totalsData = AddonUtils.calculateCategoryTotals(cbpOnboard.quoteAddons);
        textData.category_a_total = totalsData.category_a_total;
        textData.category_b_total = totalsData.category_b_total;
        textData.category_c_total = totalsData.category_c_total;
        textData.subscription_total = totalsData.all_categories_total;
        textData.weekly_fee = textData.subscription_total; // Weekly fee field used to write to zoho sub
        textData.subscription_total_pre_nsw = totalsData.all_categories_total + Number(contractDetails.NSWWeeklyIncentiveExclGST);
        textData.a1_total_value = cbpOnboard.A1TotalValue;
        textData.a2_total_value = cbpOnboard.A2TotalValue;
        textData.a3_total_value = cbpOnboard.A3TotalValue;
        textData.a3_total_value = cbpOnboard.A3TotalValue;
        textData.total_addons_value = cbpOnboard.TotalAddonsValue;

        this.pricing_record_id = cbpOnboard.PricingRecordId;

        // Use GST Inclusive price from CHB to save weekly_fee to Zoho
        const category_totals_inc_gst = AddonUtils.calculateCategoryTotalsIncGst(cbpOnboard.quoteAddons)
        textData.weekly_fee = category_totals_inc_gst.all_categories_total

    }
};


class SplendFlexiOwnUKPayload extends PlanPayload {
    isHP: boolean | undefined;
    isHPCarOnly: boolean | undefined;
    splend_initiative: string | undefined;
    insurance_doc_store_id: string | undefined;

    constructor(
        driverDetails: DriverDetails,
        personalInfo: PersonalInfo,
        carInfo: CarInfo,
        contractDetails: ContractDetails,
        addressInfo: AddressInfo,
        emergencyContactInfo: EmergencyContactInfo,
        usedVehicleAddendum: UsedVehicleAddendumDetails,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;
        textData.start_date_text = convertDateForZohoSign(contractDetails.subsStartDate);

        this.prefill_data.field_date_data.end_date = convertDateForZohoSign(contractDetails.subsEndDate);

        // Adding fields specific to SplendFlexiOwn plan
        textData.mobile = personalInfo.mobile;
        textData.vehicle_description = `${carInfo.year} ${carInfo.make} ${carInfo.model} VIN: ${carInfo.vin} Registration: ${carInfo.registrationPlate}`;
        textData.type = driverDetails.plan;
        textData.primary_emergency_name = emergencyContactInfo.primaryName;
        textData.primary_emergency_address = emergencyContactInfo.primaryAddress;
        textData.primary_emergency_phone = emergencyContactInfo.primaryPhone;
        textData.primary_emergency_relationship = emergencyContactInfo.primaryRelationship;
        textData.secondary_emergency_name = emergencyContactInfo.secondaryName;
        textData.secondary_emergency_address = emergencyContactInfo.secondaryAddress;
        textData.secondary_emergency_phone = emergencyContactInfo.secondaryPhone;
        textData.secondary_emergency_relationship = emergencyContactInfo.secondaryRelationship;
        textData.address = `${addressInfo.street} ${addressInfo.city} ${addressInfo.postcode}`;
        textData.gender = personalInfo.gender;
        textData.nationality = personalInfo.nationality;
        textData.weekly_fee = String(contractDetails.planWeeklyFee);
        textData.setup_fee = String(contractDetails.planSetupFee);
        textData.weekly_allowance = String(contractDetails.planWeeklyDistAllowance);
        textData.additional_charge = String(contractDetails.planAdditionalDistCharge);
        textData.cover_by = contractDetails.insuranceCover;
        textData.plan_length = String(contractDetails.planLen);
        textData.contact_owner = contractDetails.contactOwner;
        textData.minimum_period_weeks = String(contractDetails.planMinimumPeriod);
        textData.licence_expiry_text = flipDateFormat(driverDetails.driversLicenceExpiry);
        textData.dob_text = flipDateFormat(driverDetails.dateOfBirth);
        textData.registration = carInfo.registrationPlate;
        textData.ipb_discounted_weekly_subscription = (contractDetails.planWeeklyFee - contractDetails.ipbDiscountAmount) || 0;
        textData.weekly_fee_car = contractDetails.planWeeklyFee - 108;
        // Made optional as the directive for UK is that the field in the contract is not required.
        // textData.licence_country = driverDetails.licenceCountry;

        this.prefill_data.isHP = contractDetails.isHP || false;
        this.prefill_data.isHPCarOnly = contractDetails.isHPCarOnly || false;

        // Adding additional fields directly to the object
        this.contact_owner_id = contractDetails.contactOwnerId;
        this.execution_date = contractDetails.executionDate;
        this.licence_expiry = driverDetails.driversLicenceExpiry;
        this.contact_id = driverDetails.crmId;
        this.first_name = personalInfo.firstName;
        this.middle_name = personalInfo.middleName;
        this.last_name = personalInfo.lastName;
        this.street = addressInfo.street;
        this.postcode = addressInfo.postcode;
        this.province = addressInfo.state;
        this.end_date = contractDetails.subsEndDate;
        this.vehicle_id = carInfo.id;
        this.city = addressInfo.city;
        this.dob = driverDetails.dateOfBirth;
        this.start_date = contractDetails.subsStartDate;
        this.national_insurance_number = personalInfo.nationalInsuranceNumber;
        this.pco_licence_number = driverDetails.pcoLicenseNumber;
        this.pco_licence_expiry = driverDetails.pcoLicenceExpiryDate;
        this.vehicle_location = carInfo.hub;
        this.ipb_discount_amount = contractDetails.ipbDiscountAmount;
        this.isHP = contractDetails.isHP;
        this.isHPCarOnly = contractDetails.isHPCarOnly;

        // Set splend_initiative for HP Car Only Promo
        if (contractDetails.isHPCarOnly) {
            this.splend_initiative = "Uber HP Car Only Promo";
            this.insurance_doc_store_id = contractDetails.insuranceDocStoreId
        }
        // todo is this used in zoho sign contracts ?

    }
};


class SplendRentToOwnUsedVehiclePayload extends PlanPayload {
    constructor(
        driverDetails: DriverDetails,
        personalInfo: PersonalInfo,
        carInfo: CarInfo,
        contractDetails: ContractDetails,
        addressInfo: AddressInfo,
        emergencyContactInfo: EmergencyContactInfo,
        usedVehicleAddendum: UsedVehicleAddendumDetails,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;

        textData.end_date_text = flipDateFormat(contractDetails.subsEndDate);
        textData.licence_expiry_text = flipDateFormat(driverDetails.driversLicenceExpiry);
        textData.dob_text = flipDateFormat(driverDetails.dateOfBirth);
        // start_date_text supposed to be removed once the start_date/start_datetime fields (from parent class)
        // are made available through deployment. Make sure the ZohoSign template is updated before
        // removing this.
        textData.start_date_text = flipDateFormat(contractDetails.subsStartDate);
        textData.execution_date_text = flipDateFormat(contractDetails.executionDate);

        // Adding fields specific to SplendRentToOwnUsedVehicle plan
        textData.vehicle_description = `${carInfo.year} ${carInfo.make} ${carInfo.model} VIN: ${carInfo.vin} Registration: ${carInfo.registrationPlate}`;
        textData.type = driverDetails.plan;
        textData.primary_emergency_name = emergencyContactInfo.primaryName;
        textData.primary_emergency_address = emergencyContactInfo.primaryAddress;
        textData.primary_emergency_phone = emergencyContactInfo.primaryPhone;
        textData.primary_emergency_relationship = emergencyContactInfo.primaryRelationship;
        textData.secondary_emergency_name = emergencyContactInfo.secondaryName;
        textData.secondary_emergency_address = emergencyContactInfo.secondaryAddress;
        textData.secondary_emergency_phone = emergencyContactInfo.secondaryPhone;
        textData.secondary_emergency_relationship = emergencyContactInfo.secondaryRelationship;
        textData.address = `${addressInfo.street} ${addressInfo.city} ${addressInfo.state} ${addressInfo.postcode}`;
        textData.gender = personalInfo.gender;
        textData.nationality = personalInfo.nationality;
        textData.toll_tag_number = personalInfo.tollTagNumber;
        textData.toll_tag_account_number = personalInfo.tollTagAccountNumber;
        textData.toll_tag_issuer = personalInfo.tollTagIssuer;
        textData.abn = personalInfo.hirerABN;
        textData.weekly_fee = String(contractDetails.planWeeklyFee);
        textData.setup_fee = String(contractDetails.planSetupFee);
        textData.weekly_allowance = String(contractDetails.planWeeklyDistAllowance);
        textData.additional_charge = String(contractDetails.planAdditionalDistCharge);
        textData.cover_by = contractDetails.insuranceCover;
        textData.plan_length = String(contractDetails.planLen);
        textData.contact_owner = contractDetails.contactOwner;
        textData.nsw_ev_incentive_value_before_discount = contractDetails.NSWIncentivePlanBeforeDiscount;
        textData.nsw_ev_incentive_total = contractDetails.NSWIncentiveTotalValue;

        // Adding fields specific to used vehicle addendum
        textData.reduced_subscription_term = usedVehicleAddendum.reducedSubscriptionTerm;
        textData.discounted_weeks = usedVehicleAddendum.numberOfDiscountedWeeks;
        textData.scheduled_interval_services = usedVehicleAddendum.scheduledIntervalServices;
        textData.replacement_tyres = usedVehicleAddendum.replacementTyres;
        textData.replacement_brake_pads = usedVehicleAddendum.replacementBrakePads;
        textData.changed_brake_discs = usedVehicleAddendum.replacementOrMachined;

        textData.licence_country = driverDetails.licenceCountry;
        textData.minimum_period_weeks = String(contractDetails.planMinimumPeriod);

        this.prefill_data.field_date_data.end_date = convertDateForZohoSign(contractDetails.subsEndDate);

        // Adding additional fields directly to the object
        this.contact_owner_id = contractDetails.contactOwnerId;
        this.province = addressInfo.state;
        this.end_date = contractDetails.subsEndDate;
        this.vehicle_location = carInfo.hub;

    }
};

class FlexiOwnRentToOwnUsedVehiclePayloadV2 extends SplendRentToOwnUsedVehiclePayload {
    quoteAddons: QuoteAddon[];
    pricing_record_id: string;

    constructor(
        driverDetails: DriverDetails,
        personalInfo: PersonalInfo,
        carInfo: CarInfo,
        contractDetails: ContractDetails,
        addressInfo: AddressInfo,
        emergencyContactInfo: EmergencyContactInfo,
        usedVehicleAddendum: UsedVehicleAddendumDetails,
        selectedPotential: ICustomerPotential,
        cbpOnboard: CBPOnboard
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, usedVehicleAddendum, selectedPotential);

        // Update quoteAddons with live prices before using them for calculations
        const updatedQuoteAddons = AddonUtils.updateQuoteAddonsWithLivePrices(cbpOnboard.quoteAddons, cbpOnboard.live_prices);
        cbpOnboard.quoteAddons = updatedQuoteAddons;

        let textData = this.prefill_data.field_text_data;

        textData.mobile = personalInfo.mobile;
        textData.plan = cbpOnboard.Plan;
        textData.make_and_model = cbpOnboard.Make + " " + cbpOnboard.Model;
        textData.body_type = carInfo.bodyType;
        textData.plan_length = cbpOnboard.TermLength;

        textData.end_date_text = convertDateForZohoSign(contractDetails.subsEndDate);

        textData.weekly_fee = cbpOnboard.quoteAddons.filter(
            (item: PricingRecordLineItem) => item.included == true
        ).reduce((sum: number, item: PricingRecordLineItem) => {
            return sum + Number(item.price_excluding_gst);
        }, 0).toFixed(2);
        textData.ipb_discounted_weekly_subscription = String((Number(textData.weekly_fee) - contractDetails.ipbDiscountAmount).toFixed(2));
        textData.vehicle_driveaway_price = cbpOnboard.vehicleDriveawayPrice;
        textData.replacement_tyres = cbpOnboard.ReplacementTyres;
        textData.replacement_brake_pads = cbpOnboard.ReplacementBrakePadSet;
        textData.brake_or_machined_discs = cbpOnboard.ReplacementOrMachiningOfBrakeDiscs;

        textData.replacement_tyres = cbpOnboard.ReplacementTyres;
        textData.replacement_brake_pads = cbpOnboard.ReplacementBrakePadSet;
        textData.brake_or_machined_discs = cbpOnboard.ReplacementOrMachiningOfBrakeDiscs;

        textData.nsw_weekly_incentive_excl_gst = contractDetails.NSWWeeklyIncentiveExclGST;
        textData.nsw_total_incentive_excl_gst = contractDetails.NSWTotalIncentiveExclGST;

        textData.reduced_subscription_term = cbpOnboard.TermReduction;
        textData.reduced_term_length_weeks = cbpOnboard.ReducedTermLengthWeeks

        textData.weekly_distance_allowance = cbpOnboard.WeeklyDistanceAllowance;
        textData.excess_distance_cost = cbpOnboard.ExcessDistanceCost;
        textData.setup_fee_excluding_gst = cbpOnboard.setupFeeExcludingGst;
        textData.loss_and_damage_waiver_value = cbpOnboard.LossAndDamageWaiverValue;

        this.plan_id = cbpOnboard.PlanId; // Overrides
        // QuoteAddon mirrors the structure the BE expects for the signature document addons.
        this.quoteAddons = cbpOnboard.quoteAddons.filter(
            (item: PricingRecordLineItem) => item.included == true
        ).map((item: PricingRecordLineItem) => {
            return {
                plan_item_type: item.chargebee_item_type,
                plan_item_key: item.chargebee_item_key,
                plan_component: item.chargebee_component,
                quantity: 1
            }
        });

        const addonTextData = AddonUtils.extrapolateAddonsAsFieldTextData(cbpOnboard.quoteAddons);
        Object.assign(textData, addonTextData);

        const totalsData = AddonUtils.calculateCategoryTotals(cbpOnboard.quoteAddons);
        textData.subscription_total_pre_nsw = totalsData.all_categories_total + Number(contractDetails.NSWWeeklyIncentiveExclGST);
        textData.category_a_total = totalsData.category_a_total;
        textData.category_b_total = totalsData.category_b_total;
        textData.category_c_total = totalsData.category_c_total;
        textData.subscription_total = totalsData.all_categories_total;

        textData.a1_total_value = cbpOnboard.A1TotalValue;
        textData.a2_total_value = cbpOnboard.A2TotalValue;
        textData.a3_total_value = cbpOnboard.A3TotalValue;
        textData.a3_total_value = cbpOnboard.A3TotalValue;
        textData.total_addons_value = cbpOnboard.TotalAddonsValue;

        this.pricing_record_id = cbpOnboard.PricingRecordId;

        // Use GST Inclusive price from CHB to save weekly_fee to Zoho
        const category_totals_inc_gst = AddonUtils.calculateCategoryTotalsIncGst(cbpOnboard.quoteAddons)
        textData.weekly_fee = category_totals_inc_gst.all_categories_total
    }
}

class SplendRentToOwnUsedVehicleUKPayload extends PlanPayload {
    constructor(
        driverDetails: DriverDetails,
        personalInfo: PersonalInfo,
        carInfo: CarInfo,
        contractDetails: ContractDetails,
        addressInfo: AddressInfo,
        emergencyContactInfo: EmergencyContactInfo,
        usedVehicleAddendum: UsedVehicleAddendumDetails,
        selectedPotential: ICustomerPotential
    ) {
        super(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);

        let textData = this.prefill_data.field_text_data;

        textData.licence_expiry_text = flipDateFormat(driverDetails.driversLicenceExpiry);
        textData.dob_text = flipDateFormat(driverDetails.dateOfBirth);

        // Adding fields specific to SplendRentToOwnUsedVehicle plan
        textData.vehicle_description = `${carInfo.year} ${carInfo.make} ${carInfo.model} VIN: ${carInfo.vin} Registration: ${carInfo.registrationPlate}`;
        textData.type = driverDetails.plan;
        textData.primary_emergency_name = emergencyContactInfo.primaryName;
        textData.primary_emergency_address = emergencyContactInfo.primaryAddress;
        textData.primary_emergency_phone = emergencyContactInfo.primaryPhone;
        textData.primary_emergency_relationship = emergencyContactInfo.primaryRelationship;
        textData.secondary_emergency_name = emergencyContactInfo.secondaryName;
        textData.secondary_emergency_address = emergencyContactInfo.secondaryAddress;
        textData.secondary_emergency_phone = emergencyContactInfo.secondaryPhone;
        textData.secondary_emergency_relationship = emergencyContactInfo.secondaryRelationship;
        textData.address = `${addressInfo.street} ${addressInfo.city} ${addressInfo.postcode}`;
        textData.gender = personalInfo.gender;
        textData.nationality = personalInfo.nationality;
        textData.weekly_fee = String(contractDetails.planWeeklyFee);
        textData.setup_fee = String(contractDetails.planSetupFee);
        textData.weekly_allowance = String(contractDetails.planWeeklyDistAllowance);
        textData.additional_charge = String(contractDetails.planAdditionalDistCharge);
        textData.cover_by = contractDetails.insuranceCover;
        textData.plan_length = String(contractDetails.planLen);
        textData.contact_owner = contractDetails.contactOwner;
        textData.time_at_address_years = addressInfo.timeAtAddressYears;
        textData.time_at_address_months = addressInfo.timeAtAddressMonths;

        // Adding fields specific to used vehicle addendum
        textData.reduced_subscription_term = usedVehicleAddendum.reducedSubscriptionTerm;
        textData.discounted_weeks = usedVehicleAddendum.numberOfDiscountedWeeks;
        textData.scheduled_interval_services = usedVehicleAddendum.scheduledIntervalServices;
        textData.replacement_tyres = usedVehicleAddendum.replacementTyres;
        textData.replacement_brake_pads = usedVehicleAddendum.replacementBrakePads;
        textData.changed_brake_discs = usedVehicleAddendum.replacementOrMachined;
        textData.licence_country = driverDetails.licenceCountry;
        textData.minimum_period_weeks = String(contractDetails.planMinimumPeriod);

        this.prefill_data.field_date_data.end_date = convertDateForZohoSign(contractDetails.subsEndDate);

        // Adding additional fields directly to the object
        this.contact_owner_id = contractDetails.contactOwnerId;
        this.province = addressInfo.state;
        this.end_date = contractDetails.subsEndDate;
        this.vehicle_location = carInfo.hub;
        this.national_insurance_number = personalInfo.nationalInsuranceNumber;
        this.pco_licence_number = driverDetails.pcoLicenseNumber;
        this.pco_licence_expiry = driverDetails.pcoLicenceExpiryDate;

    }
};


type PayloadType =
    | RAVCreditHirePayload
    | FlexiPayload
    | FlexiV2Payload
    | RAVSharecoverPayload
    | RAVShortTermRentalPayload
    | SplendFlexiOwnPayload
    | SplendRentToOwnUsedVehiclePayload;

//Move everything to separate ts file and import create Payload function. That way it can be exported for test
export const createPayload = (
    planType: string,
    driverDetails: any,
    personalInfo: any,
    carInfo: any,
    contractDetails: any,
    addressInfo: any,
    selectedPotential: ICustomerPotential,
    emergencyContactInfo: any = {},
    usedVehicleAddendum: any = {},
    splendInitiatives: any = {},
    CBPOnboard: any = {},
): PayloadType => {
    sentryLogger.info("Creating payload...",
        { planType, driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential, emergencyContactInfo, usedVehicleAddendum, splendInitiatives, CBPOnboard });
    switch (planType) {
        case PlanOptions.RAVCreditHire:
            return new RAVCreditHirePayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);
        case PlanOptions.SplendFlexi:
            if (CBPOnboard.PlanId) {
                return new FlexiV2Payload(
                    driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, splendInitiatives, selectedPotential, CBPOnboard
                );
            }
            return new FlexiPayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, splendInitiatives, selectedPotential);
        case PlanOptions.RAVSharecover:
            return new RAVSharecoverPayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);
        case PlanOptions.RAVShortTermRental:
            return new RAVShortTermRentalPayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);
        case PlanOptions.SplendFlexiOwn:
            if (CBPOnboard.PlanId) {
                return new FlexiOwnV2(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, usedVehicleAddendum, selectedPotential, CBPOnboard);
            }
            return new SplendFlexiOwnPayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, usedVehicleAddendum, selectedPotential);
        case PlanOptions.SplendRentToOwnUsedVehicle:
            if (CBPOnboard.PlanId) {
                return new FlexiOwnRentToOwnUsedVehiclePayloadV2(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, usedVehicleAddendum, selectedPotential, CBPOnboard);
            }
            return new SplendRentToOwnUsedVehiclePayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, usedVehicleAddendum, selectedPotential);
        case PlanOptions.UKFlexi:
            return new SplendFlexiUKPayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, splendInitiatives, selectedPotential);
        case PlanOptions.UKFlexiOwn:
            return new SplendFlexiOwnUKPayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, usedVehicleAddendum, selectedPotential);
        case PlanOptions.UKRentToOwnUsed:
            return new SplendRentToOwnUsedVehicleUKPayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, emergencyContactInfo, usedVehicleAddendum, selectedPotential);
        case PlanOptions.PHAVCreditHire:
        case PlanOptions.PHAVShortTermRental:
            return new PHAVPayload(driverDetails, personalInfo, carInfo, contractDetails, addressInfo, selectedPotential);
        default:
            throw new Error(`Unknown plan type: ${planType}`);
    }
};
